import AbstractApiModel from '@/models/abstracts/AbstractApiModel';
import doc from '@model-core/decorators/doc';
import type { Dayjs } from 'dayjs';
import MemberModel from '@/models/entities/MemberModel';
import TeamModel from '@/models/entities/TeamModel';
import { TEAM_MEMBER_STATUS, TEAM_ROLE } from '@/models/references/references';
import { referenceOf } from '@/stores/useReferenceOptions';
import { urnOf } from '@/models/urn';

/** URN pour écrire une relation vers cette ressource (corps d'un POST / PATCH) ; un filtre prend l'id */
export const teamMemberUrn = (teamMemberId: id | number) => urnOf('team_member', teamMemberId);

/**
 * Appartenance d'un employé (Member) à une équipe (GET / POST / PATCH /team_members), avec son rôle dans l'équipe.
 * team et member (puis son user) réduits à leur identité : à résoudre par l'écran (fetchResolvable('team'),
 * fetchResolvable('member', 'user')) pour le nom de l'équipe, de l'employé et son rôle dans la société.
 * Pas de suppression : il devient Inactif quand il quitte l'équipe (POST /team_member_status_histories, historisé, trace
 * conservée) ; PATCH : le rôle seulement. team et member ne changent plus.
 * Règles de l'API (422) : une seule équipe active par employé, un seul manager par équipe, équipe active.
 */
export default class TeamMemberModel extends AbstractApiModel {
  protected _path = '/team_members/{?id}';

  @doc.model(() => TeamModel) declare team: TeamModel;
  @doc.model(() => MemberModel) declare member: MemberModel;

  declare _tsFilters: {
    team?: string;
    member?: string;
    'member.user'?: string;
    'team.company'?: string;
    teamMemberStatus?: id;
    teamRole?: id;
  };
  declare _tsSorts: { 'member.user.lastName'?: unknown; 'member.user.firstName'?: unknown };

  @doc.date declare createdAt: Dayjs;
  @doc.string declare createdBy: string;
  @doc.date declare updatedAt: Dayjs;
  @doc.string declare updatedBy: string;

  /** Toujours dans l'équipe (statut Actif) */
  get isActive(): boolean {
    return (
      referenceOf('teamMemberStatuses', this.get<unknown, unknown>('teamMemberStatus', null))?.reference ===
      TEAM_MEMBER_STATUS.ACTIVE
    );
  }

  get teamId(): number {
    return Number(this.team.id) || 0;
  }

  get teamName(): string {
    return this.team.name;
  }

  get teamColor(): string {
    return this.team.color;
  }

  /** Id du Member (rattachement à la société) : fiche employé /employees/{id} */
  get memberId(): number {
    return Number(this.member.id) || 0;
  }

  get firstName(): string {
    return this.member.firstName;
  }

  get lastName(): string {
    return this.member.lastName;
  }

  get email(): string {
    return this.member.email;
  }

  /** NOM Prénom : convention du projet */
  get fullName(): string {
    return `${this.lastName} ${this.firstName}`.trim();
  }

  /** MANAGER / TEAMMATE */
  get teamRoleReference(): string {
    return referenceOf('teamRoles', this.get<unknown, unknown>('teamRole', null))?.reference ?? '';
  }

  get teamRoleLabel(): string {
    return referenceOf('teamRoles', this.get<unknown, unknown>('teamRole', null))?.label ?? '';
  }

  get isManager(): boolean {
    return this.teamRoleReference === TEAM_ROLE.MANAGER;
  }
}
